import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Scissors, Globe, Briefcase, LayoutDashboard, FilePlus,
  Package, CreditCard, Mail, FileText, LogOut,
} from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";

const MAIN_NAV = [
  { to: "/admin", label: "Alle Salons", icon: Globe, end: true },
  { to: "/admin/industries", label: "Berufsgruppen", icon: Briefcase },
  { to: "/admin/landing", label: "Landing", icon: LayoutDashboard },
  { to: "/admin/pages", label: "Seiten", icon: FilePlus },
];

const MANAGEMENT_NAV = [
  { to: "/admin/plans", label: "Pakete", icon: Package },
  { to: "/admin/stripe", label: "Stripe", icon: CreditCard },
  { to: "/admin/email-settings", label: "SMTP", icon: Mail },
  { to: "/admin/email-templates", label: "Vorlagen", icon: FileText },
];

function NavSection({ title, items }) {
  return (
    <div className="mb-6">
      <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--mv-text-muted)" }}>
        {title}
      </p>
      <div className="space-y-0.5">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`sidebar-link-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "font-medium" : ""}`
            }
            style={({ isActive }) => ({
              color: isActive ? "var(--mv-primary)" : "var(--mv-text)",
              background: isActive ? "rgba(28,84,187,0.1)" : "transparent",
            })}
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside
      className="w-60 flex flex-col"
      style={{
        background: "#ffffff",
        borderRight: "1px solid var(--mv-border)",
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        overflowY: "auto",
      }}
      data-testid="admin-sidebar"
    >
      <div className="flex items-center gap-2.5 px-4 py-5">
        <PlatformLogo imgClassName="h-8 w-auto object-contain">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--mv-primary)" }}
          >
            <Scissors className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight truncate">StudioMV</p>
            <p className="text-[10px] uppercase tracking-[0.12em] leading-tight" style={{ color: "var(--mv-text-muted)" }}>Plattform</p>
          </div>
        </PlatformLogo>
      </div>

      <nav className="flex-1 px-2 pt-2 overflow-y-auto">
        <NavSection title="Hauptbereich" items={MAIN_NAV} />
        <NavSection title="Verwaltung" items={MANAGEMENT_NAV} />
      </nav>

      <div className="px-3 py-4" style={{ borderTop: "1px solid var(--mv-border)" }}>
        <div className="flex items-center gap-2.5 mb-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold text-white"
            style={{ background: "var(--mv-primary)" }}
          >
            {(user?.email || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="sidebar-logout-btn"
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors hover:bg-[var(--mv-surface)]"
          style={{ color: "var(--mv-text-muted)" }}
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Abmelden
        </button>
      </div>
    </aside>
  );
}
